import React from 'react';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import { canUserEditEntity, userHasRoles } from 'services/Authorization';
import strings from 'strings';
import { FPIcon, When } from 'UI/components/atoms';
import { InfoLabelModes } from 'UI/components/molecules/InfoLabel';
import { KeywordTags } from 'UI/components/molecules/KeywordTags';
import { ProfileAlert } from 'UI/components/molecules/ProfileAlert';
import InfoLabelsGrid from 'UI/components/organisms/inventoryProfiles/InfoLabelsGrid';
import InventoryOverviewLayout from 'UI/components/organisms/inventoryProfiles/InventoryOverviewLayout';
import {
  buildEmailLabels,
  buildKeywordTagsCard,
  extractFullName,
  getEmailValidationAlerts,
  getPhonesLabelsForProfile
} from 'UI/components/organisms/inventoryProfiles/utils';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { SvgInformationCircle } from 'UI/res';
import { phoneNumberFormatter } from 'UI/utils';
import { buildLocationInfoLabel } from 'UI/utils/inventory';

import DataSheetWrapper from './DataSheetWrapper';

const DATA_SHEET_KEY = 'dataSheet';

const {
  shared: {
    fields: { currentCompany: currentCompanyString }
  }
} = strings;

export const DefaultCards = ({
  profile,
  loading,
  onEditClick,
  onWriteUpClick,
  candidateDataSheet
}) => {
  const [currentUser] = useUserContext();

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const isMultiplePhoneEnabled = checkIfFeatureFlagEnabled(FeatureFlags.CandidatesMultiplePhones);

  const hasExpandedPhones = Array.isArray(profile?.phones) && profile.phones.length > 0;

  const contactData = {
    phone: profile?.personalInformation?.contact?.phone,
    otherPhone: profile?.personalInformation?.contact?.mobile
  };

  const fullName = extractFullName(profile, EntityType.Candidate);

  const userHasRightsToEdit = canUserEditEntity(currentUser, profile, {
    includeCollaborator: true
  });

  const industryAndSpecialty = [profile?.specialty?.industry?.title, profile?.specialty?.title]
    .filter(Boolean)
    .join(' · ');

  const generalLabels = [
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.nickName,
      description: profile?.personalInformation?.nickname
    },
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.title,
      description: profile?.title
    },
    {
      title: strings.shared.fields.jobFunction,
      description: profile?.position?.title
    },
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.industry,
      description: industryAndSpecialty
    },
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.subspecialty,
      description: profile?.subspecialty?.title
    },
    {
      title: currentCompanyString,
      mode: profile?.company_id ? InfoLabelModes.LOCAL_LINK : InfoLabelModes.TEXT,
      description: profile?.current_company,
      itemId: profile?.company_id,
      linkProps: profile?.company_id
        ? {
            url: EntityRoutes.CompanyProfile
          }
        : null
    },
    buildLocationInfoLabel({
      city: profile?.personalInformation?.address?.city?.title,
      state: profile?.personalInformation?.address?.city?.state?.slug,
      country: profile?.personalInformation?.address?.city?.state?.country?.slug
    }),
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.zipCode,
      description: profile?.personalInformation?.address?.zip
    },
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.source,
      description: profile?.sourceType?.title
    },
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.linkedIn,
      mode: InfoLabelModes.EXTERNAL_LINK,
      linkProps: {
        url: profile?.link_profile ?? ''
      }
    },
    ...buildEmailLabels({ profile, entityType: EntityType.Candidate })
  ];

  const legacyPhoneLabels = [
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.phone,
      description: ` ext: ${profile?.personalInformation?.contact?.ext ?? ''} `,
      mode: InfoLabelModes.RING_CENTRAL,
      linkProps: {
        url: phoneNumberFormatter(contactData?.phone) ?? '',
        id: profile?.id,
        role: EntityType.Candidate,
        customLinkProps: {
          profileName: fullName
        }
      }
    },
    {
      title: strings.inventoryProfiles.sections.overview.candidateOverview.otherPhone,
      mode: contactData?.otherPhone ? InfoLabelModes.RING_CENTRAL : InfoLabelModes.TEXT,
      linkProps: {
        url: phoneNumberFormatter(contactData?.otherPhone) ?? '',
        id: profile?.id,
        role: EntityType.Candidate,
        customLinkProps: {
          profileName: fullName
        }
      }
    }
  ];

  const canEdit = userHasRightsToEdit || userHasRoles([Roles.Operations, Roles.DataCoordinator]);

  const alerts = getEmailValidationAlerts({
    profile,
    loading
  }).map(item => ({
    componentProps: item,
    Component: ProfileAlert
  }));

  const shouldUseMultiplePhones = isMultiplePhoneEnabled && hasExpandedPhones;

  const phonesLabels = shouldUseMultiplePhones
    ? getPhonesLabelsForProfile(profile, EntityType.Candidate)
    : legacyPhoneLabels;

  const dataSheetAlert = {
    Component: () => (
      <When condition={!loading}>
        <ProfileAlert
          icon={<FPIcon component={SvgInformationCircle} onSetColor={color => color.info.main} />}
          severity="info"
          primaryText={strings.candidates.profile.dataSheet.hint}
        />
      </When>
    )
  };

  const generalInformationCard = [
    {
      Component: () => (
        <When condition={!loading}>
          <DataSheetWrapper
            progress={candidateDataSheet?.progress}
            onWriteUpClick={onWriteUpClick}
            onEditClick={canEdit ? onEditClick : undefined}
          />
        </When>
      ),
      componentProps: { key: DATA_SHEET_KEY }
    },
    ...alerts,
    dataSheetAlert,
    ...buildKeywordTagsCard({
      Component: KeywordTags,
      keywordTags: profile?.keywordTags
    }),
    {
      cardProps: {
        headerProps: {
          title: strings.inventoryProfiles.sections.overview.candidateOverview.general
        }
      },
      Component: InfoLabelsGrid,
      componentProps: {
        labels: [...generalLabels, ...phonesLabels],
        loading
      },
      className: 'generalCard'
    }
  ];

  return <InventoryOverviewLayout items={generalInformationCard} loading={loading} />;
};
